import { RefCallback, useCallback, useEffect, useRef, useState } from "react";

interface ResizeObserverOptions {
  onResize?: (entry: ResizeObserverEntry) => void;
}

interface ResizeObserverResult {
  ref: RefCallback<Element>;
  width?: number;
  height?: number;
  entry?: ResizeObserverEntry;
}

function useCustomResizeObserver<T extends Element = Element>(
  options: ResizeObserverOptions = {}
): ResizeObserverResult {
  const [entry, setEntry] = useState<ResizeObserverEntry | undefined>(
    undefined
  );
  const [node, setNode] = useState<T | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  const { onResize } = options;

  const disconnect = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
  }, []);

  const observe = useCallback(() => {
    if (node && !observerRef.current) {
      observerRef.current = new ResizeObserver(([newEntry]) => {
        setEntry(newEntry);
        if (onResize) {
          onResize(newEntry);
        }
      });
      observerRef.current.observe(node);
    }
  }, [node, onResize]);

  useEffect(() => {
    observe();
    return () => disconnect();
  }, [observe, disconnect]);

  const ref = useCallback(
    (element: T | null) => {
      if (element) {
        setNode(element);
      } else {
        disconnect();
        setNode(null);
      }
    },
    [disconnect]
  );
  return {
    ref: ref as RefCallback<Element>,
    width: entry?.contentRect?.width,
    height: entry?.contentRect?.height,
    entry,
  };
}

export default useCustomResizeObserver;
